import { mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";
import { fileURLToPath } from "node:url";
import type { Command } from "commander";
import { loadIdentity } from "../identity.js";

/**
 * Identifies a generated shim, so re-linking can tell it from an older inlined
 * copy. Keyed on its own line rather than the import statement: the shim's
 * import style already changed once (static re-export to dynamic, to fix ESM
 * hoisting) and that silently broke this check -- re-linking a shim reported
 * "replaced an inlined copy".
 */
const SHIM_MARKER = "// murmur:shim";

/**
 * The installed extension, as a one-line re-export of this installation.
 *
 * `link pi` used to copy the built extension into
 * `~/.pi/agent/extensions/murmur.ts`, making it a point-in-time snapshot:
 * upgrading murmur left the OLD extension running with no warning. The author's
 * machine ran one missing two committed fixes -- exactly the silently wrong
 * state report the extension exists to prevent.
 *
 * A shim inverts that. The file pi loads never changes and the code it points at
 * is whatever the current install has, so `npm install -g` is the whole upgrade.
 * The path survives upgrades because npm replaces a package's contents in place
 * rather than versioning the directory.
 *
 * One line on purpose: any logic here is logic that cannot be upgraded.
 */
function shim(entry: string, storePath: string): string {
  return `${SHIM_MARKER}
// Generated by \`murmur link pi\`. Do not edit.
//
// A re-export, not a copy: the extension code lives in the murmur install, so
// upgrading murmur upgrades the extension with no reinstall step. Re-run
// \`murmur link pi\` only if the install path itself moves.
//
// The store path is set here rather than resolved by the extension: a bare
// specifier cannot resolve from ~/.pi/agent/extensions, and the failure is
// silent -- the import throws, the extension swallows it, and every report is
// dropped while the tmux badge still paints.
//
// A dynamic import, not \`export ... from\`: ESM hoists static re-exports above
// this assignment, so the extension read undefined. Verified -- the static form
// printed \`undefined\` in the target.
process.env.MURMUR_STORE_MODULE ??= ${JSON.stringify(storePath)};

const { default: extension } = await import(${JSON.stringify(entry)});
export default extension;
`;
}

export function registerLink(program: Command): void {
  program
    .command("link")
    .description("Install a murmur integration")
    .argument("<target>", "integration to install")
    .option(
      "--copy",
      "inline the extension instead of re-exporting it (pins to this version; needs re-linking after an upgrade)",
    )
    .action((target: string, options: { copy?: boolean }) => {
      if (target !== "pi") throw new Error(`unsupported link target: ${target}`);
      const destination = join(
        process.env.MURMUR_PI_HOME ?? homedir(),
        ".pi",
        "agent",
        "extensions",
        "murmur.ts",
      );
      mkdirSync(dirname(destination), { recursive: true });

      const entry = fileURLToPath(new URL("./extension/murmur-pi.js", import.meta.url));
      const storePath = fileURLToPath(new URL("./extension/store.js", import.meta.url));

      // The extension reads identity rather than minting one, because an agent
      // must not decide what this node is called -- but the consequence is
      // silent: with no identity it loads, registers handlers and reports
      // nothing while the badge still paints. Linking is the only moment a human
      // is looking at this path.
      const identityMissing = loadIdentity() === null;

      if (!options.copy) {
        // Say when this replaced an inlined copy: anyone linked before the shim
        // has a snapshot that silently stopped tracking upgrades, and "wrote a
        // file" does not tell them their agents may have been misreporting. Best
        // effort -- an unreadable file is the normal first install.
        let replacedCopy = false;
        try {
          const existing = readFileSync(destination, "utf8");
          replacedCopy = !existing.includes(SHIM_MARKER);
        } catch {
          // No previous install.
        }
        writeFileSync(destination, shim(entry, storePath));
        console.log(destination);
        if (replacedCopy) {
          console.log(
            "Replaced an inlined copy from an older murmur. That copy was pinned to the version that wrote it, so it had stopped picking up fixes; running agents keep the old code until they restart.",
          );
        }
        if (identityMissing) {
          console.log(
            "This node has no identity yet, so the extension will record nothing. Run: murmur init",
          );
        }
        return;
      }

      // The copy path, for the one case the shim cannot serve: an extension that
      // must keep working when the murmur install is gone or moved.
      //
      // Pins the store import to this installation's absolute path, because a
      // bare specifier cannot resolve from ~/.pi/agent/extensions even for a
      // global install. Unpinned, every write silently no-ops while the badge
      // still paints.
      const source = readFileSync(entry, "utf8");
      const pinned = source.replace(
        /"@martintrojer\/murmur\/extension-store"/,
        JSON.stringify(storePath),
      );
      if (pinned === source) {
        throw new Error("link pi: could not pin the store import; extension build changed");
      }
      writeFileSync(destination, pinned);
      console.log(destination);
      if (identityMissing) {
        console.log(
          "This node has no identity yet, so the extension will record nothing. Run: murmur init",
        );
      }
    });
}
